import {
    CheckCircleFilled,
    CloseCircleFilled,
    ExportOutlined,
    LinkOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Descriptions,
    Flex,
    Form,
    Input,
    InputNumber,
    Popconfirm,
    Space,
    Spin,
    Steps,
    Tag,
    theme,
    Tooltip,
    Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, {useState} from 'react';

import {ApiQuery} from '../../../gen/api';
import {
    AdminEbayAccount,
    AdminEbayAccountSetup,
    AdminEbayEnvironment,
    AdminEbaySetupReq,
} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {formatAgo} from '../dashboard/helpers/slowSelling';
import {SellingLimitCard} from './SellingLimitCard';
import {signInDaysLeft, signInWarning} from './signIn';

const {Text, Title, Paragraph} = Typography;

/** The consent link carries a state valid for 15 minutes; refresh it well before. */
const CONSENT_URL_REFRESH_MS = 10 * 60_000;

const ENVIRONMENT_TAG: Record<AdminEbayEnvironment, {color: string; label: string}> = {
    sandbox: {color: 'orange', label: 'SANDBOX'},
    production: {color: 'green', label: 'PRODUCTION'},
};

/** "sell.inventory" from "https://api.ebay.com/oauth/api_scope/sell.inventory". */
function scopeName(scope: string): string {
    const name = scope.split('/api_scope/')[1];
    return name ?? 'public data';
}

function formatDate(value?: string): React.ReactNode {
    if (!value) {
        return <Text type="secondary">—</Text>;
    }
    return <Tooltip title={dayjs(value).format('MM/DD/YYYY HH:mm:ss')}>{formatAgo(value)}</Tooltip>;
}

/** The backend's message, capitalized: Go error strings start in lower case. */
const errorMessage = (errors: Array<{message?: string}>) => {
    const message = errors?.[0]?.message;
    return message && message.charAt(0).toUpperCase() + message.slice(1);
};

/** Two steps: sign in on eBay, then paste the address of the page eBay opens. */
const ConnectSteps: React.FC<{environment?: AdminEbayEnvironment; onConnected: () => void}> = ({
    environment,
    onConnected,
}) => {
    const notify = useNotification();
    const [redirect, setRedirect] = useState('');
    const consentQuery = ApiQuery.useAdminEbayConnectUrlQuery({refetchInterval: CONSENT_URL_REFRESH_MS});
    const connectMutation = ApiQuery.useAdminEbayConnectMutation({
        onSuccess: (data) => {
            notify.openNotificationWithIcon('success', `eBay account ${data.account?.username ?? ''} connected`);
            setRedirect('');
            onConnected();
        },
        onError: (errors) => notify.openNotificationWithIcon('error', 'Failed to connect', errorMessage(errors)),
    });

    const who = environment === 'sandbox' ? 'the sandbox test user' : 'the store eBay account';

    return (
        <Steps
            direction="vertical"
            current={-1}
            items={[
                {
                    title: 'Sign in on eBay',
                    description: (
                        <Flex
                            vertical
                            gap={8}
                            style={{paddingBottom: 16}}
                        >
                            <Text type="secondary">
                                Opens eBay in a new tab. Sign in with {who} and press <b>Agree</b>.
                            </Text>
                            <div>
                                <Button
                                    type="primary"
                                    icon={<ExportOutlined />}
                                    href={consentQuery.data?.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!consentQuery.data?.url}
                                    loading={consentQuery.isLoading}
                                >
                                    Open eBay sign-in
                                </Button>
                            </div>
                            {consentQuery.isError && (
                                <Text type="danger">
                                    {errorMessage(consentQuery.error) ?? 'Failed to get the link'}
                                </Text>
                            )}
                        </Flex>
                    ),
                },
                {
                    title: 'Paste the address of the page eBay opens',
                    description: (
                        <Flex
                            vertical
                            gap={8}
                        >
                            <Text type="secondary">
                                After you agree, eBay opens a page whose address contains <Text code>code=</Text>. Copy
                                the whole address from the address bar and paste it here within 5 minutes: the code
                                works once and expires quickly.
                            </Text>
                            <Input.TextArea
                                value={redirect}
                                onChange={(e) => setRedirect(e.target.value)}
                                placeholder="https://auth2.ebay.com/oauth2/ThirdPartyAuthSucessFailure?isAuthSuccessful=true&state=...&code=...&expires_in=299"
                                autoSize={{minRows: 2, maxRows: 5}}
                                style={{maxWidth: 720}}
                            />
                            <div>
                                <Button
                                    type="primary"
                                    icon={<LinkOutlined />}
                                    disabled={!redirect.trim()}
                                    loading={connectMutation.isLoading}
                                    onClick={() => connectMutation.mutate({params: {redirect: redirect.trim()}})}
                                >
                                    Connect
                                </Button>
                            </div>
                        </Flex>
                    ),
                },
            ]}
        />
    );
};

const AccountDetails: React.FC<{account: AdminEbayAccount}> = ({account}) => {
    const {token} = theme.useToken();
    const daysLeft = signInDaysLeft(account);
    const expiresSoon = !!signInWarning(account);

    return (
        <Descriptions
            column={{xs: 1, md: 2}}
            size="small"
            items={[
                {
                    key: 'username',
                    label: 'Account',
                    children: (
                        <Space>
                            {account.reconnectRequiredAt ? (
                                <CloseCircleFilled style={{color: token.colorError}} />
                            ) : (
                                <CheckCircleFilled style={{color: token.colorSuccess}} />
                            )}
                            <Text strong>{account.username}</Text>
                        </Space>
                    ),
                },
                {key: 'userId', label: 'eBay user ID', children: <Text copyable>{account.ebayUserId}</Text>},
                {
                    key: 'connected',
                    label: 'Connected',
                    children: (
                        <span>
                            {formatDate(account.connectedAt)} by {account.connectedByName}
                        </span>
                    ),
                },
                {key: 'verified', label: 'Last check', children: formatDate(account.verifiedAt)},
                {
                    key: 'expires',
                    label: 'Sign-in valid until',
                    children: (
                        <Space wrap>
                            <span style={{whiteSpace: 'nowrap'}}>
                                {dayjs(account.refreshTokenExpiresAt).format('MM/DD/YYYY')}
                            </span>
                            <Tag color={expiresSoon ? 'red' : 'default'}>
                                {account.reconnectRequiredAt
                                    ? 'refused by eBay'
                                    : daysLeft > 0
                                      ? `${daysLeft} days left`
                                      : 'expired'}
                            </Tag>
                        </Space>
                    ),
                },
                {
                    key: 'scopes',
                    label: 'Permissions',
                    children: (
                        <Flex
                            wrap
                            gap={4}
                        >
                            {account.scopes.map((scope) => (
                                <Tooltip
                                    key={scope}
                                    title={scope}
                                >
                                    <Tag>{scopeName(scope)}</Tag>
                                </Tooltip>
                            ))}
                        </Flex>
                    ),
                },
            ]}
        />
    );
};

/** Set up form defaults: ship from the warehouse in Harrisburg, NC. */
const SETUP_DEFAULTS: AdminEbaySetupReq = {handlingTimeDays: 1, shipFromPostalCode: '28075'};

const SetupForm: React.FC<{setup?: AdminEbayAccountSetup; onDone: () => void}> = ({setup, onDone}) => {
    const notify = useNotification();
    const setupMutation = ApiQuery.useAdminEbaySetupMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('success', 'eBay account is set up for publishing');
            onDone();
        },
        onError: (errors) => notify.openNotificationWithIcon('error', 'Set up failed', errorMessage(errors)),
    });

    return (
        <Form<AdminEbaySetupReq>
            layout="inline"
            initialValues={setup ?? SETUP_DEFAULTS}
            onFinish={(values) => setupMutation.mutate({params: values})}
            style={{rowGap: 8}}
        >
            <Form.Item
                name="handlingTimeDays"
                label="Ships within"
                rules={[{required: true}]}
            >
                <InputNumber
                    min={1}
                    max={5}
                    addonAfter="business days"
                    style={{width: 170}}
                />
            </Form.Item>
            <Form.Item
                name="shipFromPostalCode"
                label="Ships from ZIP"
                rules={[{required: true, pattern: /^\d{5}$/, message: '5 digits'}]}
            >
                <Input style={{width: 90}} />
            </Form.Item>
            <Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SettingOutlined />}
                    loading={setupMutation.isLoading}
                >
                    {setup ? 'Save' : 'Set up'}
                </Button>
            </Form.Item>
        </Form>
    );
};

/** Business policies and the ship-from location publishing needs. */
const SetupCard: React.FC<{account: AdminEbayAccount; onChanged: () => void}> = ({account, onChanged}) => {
    const [editing, setEditing] = useState(false);
    const setup = account.setup;

    return (
        <Card
            title={
                <Space>
                    <SettingOutlined />
                    Selling setup
                </Space>
            }
            extra={
                setup && <Button onClick={() => setEditing((value) => !value)}>{editing ? 'Cancel' : 'Change'}</Button>
            }
        >
            {setup ? (
                <Descriptions
                    column={{xs: 1, md: 2}}
                    size="small"
                    items={[
                        {
                            key: 'shipping',
                            label: 'Shipping',
                            children: (
                                <Tooltip title={`Policy ${setup.fulfillmentPolicyId}`}>
                                    USPS Ground Advantage, free unless a listing sets the buyer&apos;s cost; ships
                                    within {setup.handlingTimeDays} business day{setup.handlingTimeDays > 1 ? 's' : ''}
                                </Tooltip>
                            ),
                        },
                        {
                            key: 'returns',
                            label: 'Returns',
                            children: (
                                <Tooltip title={`Policy ${setup.returnPolicyId}`}>
                                    30 days, buyer pays return shipping
                                </Tooltip>
                            ),
                        },
                        {
                            key: 'payment',
                            label: 'Payment',
                            children: (
                                <Tooltip title={`Policy ${setup.paymentPolicyId}`}>Immediate payment required</Tooltip>
                            ),
                        },
                        {
                            key: 'location',
                            label: 'Ships from',
                            children: (
                                <Tooltip title={`Inventory location ${setup.merchantLocationKey}`}>
                                    ZIP {setup.shipFromPostalCode}, US
                                </Tooltip>
                            ),
                        },
                        {key: 'setUpAt', label: 'Set up', children: formatDate(setup.setUpAt)},
                        {
                            key: 'orders',
                            label: 'Orders imported',
                            children: setup.ordersSyncedAt ? (
                                <Tooltip title="eBay orders of listings published from the admin are imported every 15 minutes.">
                                    <span>{formatDate(setup.ordersSyncedAt)}</span>
                                </Tooltip>
                            ) : (
                                <Text type="secondary">not yet</Text>
                            ),
                        },
                    ]}
                />
            ) : (
                <Paragraph type="secondary">
                    Publishing needs shipping, payment and return policies and a ship-from location on eBay. Set up opts
                    the account in to business policies and creates them: USPS Ground Advantage (free by default),
                    immediate payment, 30-day returns paid by the buyer. The account&apos;s other policies stay
                    untouched.
                </Paragraph>
            )}
            {(!setup || editing) && (
                <SetupForm
                    setup={setup}
                    onDone={() => {
                        setEditing(false);
                        onChanged();
                    }}
                />
            )}
        </Card>
    );
};

export const EbayPage: React.FC = () => {
    const notify = useNotification();
    const [reconnecting, setReconnecting] = useState(false);

    const accountQuery = ApiQuery.useAdminEbayAccountQuery({staleTime: 0});
    const status = accountQuery.data;
    const account = status?.account;

    const onConnected = () => {
        setReconnecting(false);
        accountQuery.refetch();
    };
    const verifyMutation = ApiQuery.useAdminEbayVerifyMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('success', 'The eBay connection works');
            accountQuery.refetch();
        },
        onError: (errors) =>
            notify.openNotificationWithIcon('error', 'eBay connection check failed', errorMessage(errors)),
    });
    const disconnectMutation = ApiQuery.useAdminEbayDisconnectMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('info', 'eBay account disconnected');
            accountQuery.refetch();
        },
        onError: (errors) => notify.openNotificationWithIcon('error', 'Failed to disconnect', errorMessage(errors)),
    });

    const environment = status?.environment ? ENVIRONMENT_TAG[status.environment] : undefined;
    const warning = account && signInWarning(account);

    return (
        <Flex
            vertical
            gap={16}
        >
            <Flex
                justify="space-between"
                align="center"
                wrap
                gap={8}
            >
                <Space align="center">
                    <Title
                        level={3}
                        style={{margin: 0}}
                    >
                        eBay
                    </Title>
                    {environment && <Tag color={environment.color}>{environment.label}</Tag>}
                    {status?.marketplaceId && <Text type="secondary">{status.marketplaceId}</Text>}
                </Space>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => accountQuery.refetch()}
                    loading={accountQuery.isFetching}
                >
                    Refresh
                </Button>
            </Flex>

            {accountQuery.isLoading && <Spin />}

            {accountQuery.isError && (
                <Alert
                    type="error"
                    showIcon
                    message="Failed to load the eBay account"
                    description={errorMessage(accountQuery.error)}
                />
            )}

            {status && !status.configured && (
                <Alert
                    type="warning"
                    showIcon
                    message="Selling on eBay is not configured"
                    description={
                        <>
                            <Paragraph style={{marginBottom: 4}}>{status.configError}</Paragraph>
                            <Text type="secondary">
                                Fill in the EbaySelling section of the backend config (ClientSecret and TokenKey can
                                come from EBAYSELLING_CLIENTSECRET and EBAYSELLING_TOKENKEY) and restart the backend.
                            </Text>
                        </>
                    }
                />
            )}

            {status?.configured && account && (
                <Card
                    title={
                        <Space>
                            <SafetyCertificateOutlined />
                            Connected seller account
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button
                                onClick={() => verifyMutation.mutate(undefined)}
                                loading={verifyMutation.isLoading}
                            >
                                Check connection
                            </Button>
                            <Button onClick={() => setReconnecting((value) => !value)}>
                                {reconnecting ? 'Cancel reconnect' : 'Reconnect'}
                            </Button>
                            <Popconfirm
                                title="Disconnect the eBay account?"
                                description="Publishing to eBay stops until an account is connected again."
                                okText="Disconnect"
                                okButtonProps={{danger: true}}
                                onConfirm={() => disconnectMutation.mutate(undefined)}
                            >
                                <Button
                                    danger
                                    loading={disconnectMutation.isLoading}
                                >
                                    Disconnect
                                </Button>
                            </Popconfirm>
                        </Space>
                    }
                >
                    {warning && (
                        <Alert
                            type={warning.type}
                            showIcon
                            message={warning.message}
                            description={warning.description}
                            action={
                                !reconnecting && (
                                    <Button
                                        type="primary"
                                        onClick={() => setReconnecting(true)}
                                    >
                                        Reconnect
                                    </Button>
                                )
                            }
                            style={{marginBottom: 16}}
                        />
                    )}
                    <AccountDetails account={account} />
                </Card>
            )}

            {status?.configured && account && !reconnecting && (
                <SetupCard
                    account={account}
                    onChanged={() => accountQuery.refetch()}
                />
            )}

            {status?.configured && account && !reconnecting && !account.reconnectRequiredAt && <SellingLimitCard />}

            {status?.configured && (!account || reconnecting) && (
                <Card title={account ? 'Reconnect the seller account' : 'Connect the eBay seller account'}>
                    {!account && (
                        <Paragraph type="secondary">
                            Listings are published on behalf of this account. It signs in once; the sign-in stays valid
                            for 18 months.
                        </Paragraph>
                    )}
                    <ConnectSteps
                        environment={status.environment}
                        onConnected={onConnected}
                    />
                </Card>
            )}
        </Flex>
    );
};
